import { IMap, LoginResponseInterface, Order, OrderData } from '../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../order/useOrderFlow';
import {
  ERROR_CODES,
  ORDER_PAYMENT_STATES,
  ORDER_PAYMENT_STATUSES,
  PaymentGateway,
  PaymentMethod,
  PaymentResultErrorCode,
  PaymentStatus,
  PaypalVariant,
  SCREENS,
} from '../../services/exports/Constants';
import useRouter from '../navigation/useRouter';
import useAlert from '../utility/useAlert';
import { PaymentIntent } from '@stripe/stripe-js';
import {
  RenderableErrorResponse,
  ReservedHttpResponseCodes,
  ValidationErrorResponseData,
} from '../../services/exports/Types';
import useInitialData from '../global/useInitialData';
import { AxiosError, AxiosResponse } from 'axios';
import useAppMethods from '../utility/useAppMethods';
import { OTP_VERIFICATION_PHONE_NUMBER_HEADER } from '../../modals/OtpVerificationModal';
import useOtpStepUp from './useOtpStepUp';
import { SectionType } from '../../components-library/checkout/sections/CheckoutInfoBag';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { RedirectStatus } from '../../pages/OrderDetailsScreen';
import collect from 'collect.js';
import useAuth from '../global/useAuth';
import useMobileApp from '../mobile-app/useMobileApp';
import { Modals, Routes } from '../../modules/mobile/exports/Enums';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import useDeliveryOrderMethodValidation from '../../modules/mobile/modals/order-method/useDeliveryOrderMethodValidation';
import useOrderErrorHandler from '../../modules/mobile/common/hooks/error-handlers/useOrderErrorHandler';
import useAnalytics from '../analytics/useAnalytics';
import useBasket from '../basket/useBasket';
import { setUtm } from '../../services/exports/Cookies';
import usePaymentFlow from './usePaymentFlow';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useNavigate from '../navigation/useNavigate';
import { useCallback, useRef } from 'react';
import WindowResource from '../../services/resources/WindowResource';
import str from '../../services/helpers/Stringable';
import {
  ConfirmPaymentFunction,
  GenerateOrderReturnUrlFunction,
  PaymentErrorOptions,
  PaymentReturnUrlPlaceholder,
} from './useCheckout';
import { withoutOffsiteReturnParams } from './useSpreedlyOffsitePayment';
import toast from 'react-hot-toast';
import paymentLog, { PaymentGatewayKey } from '../../services/helpers/PaymentLogger';
import useOrderMethodErrorHandlers from '../order/useOrderMethodErrorHandlers';
import { resolveAnalyticsPaymentGateway } from '../../utils/analytics/paymentGateway';
import { STRIPE_ELEMENT_RULES, stripeElementValidationMessage } from '../../utils/stripe';
import { isFreeOrderDraft, resolveCheckoutPaymentMethod } from '../../services/helpers/checkoutPaymentMethod';
import OrderManager from '../../services/api/OrderManager';

type Props = {
  setLoading: (loading: boolean) => void;
  onMobileAppCheckoutError: (response: RenderableErrorResponse) => void;
};

export type CheckoutOptions = {
  /**
   * Fired right before the OTP modal opens in response to a 400
   * OTP_VERIFICATION_REQUIRED. Used by Adyen wallet flows (Apple Pay /
   * Google Pay) to fake-resolve the wallet sheet so the user can interact
   * with the OTP modal — Adyen has no API to dismiss a wallet sheet
   * without finalizing. No-op for everything else.
   */
  onBeforeOtpModalOpen?: () => void;

  /**
   * Fires after the order endpoint returns anything other than a
   * 400 OTP_VERIFICATION_REQUIRED. Used by the OTP modal to close as soon
   * as the OTP gate is cleared instead of waiting for the downstream
   * confirm chain (which for wallet/3DS flows is async and may take a
   * long time). The actual order outcome still resolves via the outer
   * deferred promise.
   */
  onOtpAccepted?: () => void;
};

export type CheckoutFunction = (
  confirmPayment: ConfirmPaymentFunction,
  paymentIntent?: PaymentIntent,
  extraData?: Partial<OrderData>,
  extraHeaders?: IMap<string>,
  options?: CheckoutOptions,
) => Promise<boolean>;

export function useGenerateOrderReturnUrl() {
  const { isMobileApp } = useMobileApp();
  const { generatePath } = useNavigate();

  return {
    generateReturnUrl: useCallback<GenerateOrderReturnUrlFunction>(
      (replace = {}) => {
        const base = isMobileApp
          ? `${WindowResource.getOriginWithPathname()}?open_modal=${Modals.OrderTracker}&uuid=${PaymentReturnUrlPlaceholder.OrderUUID}&payment_gateway=${PaymentReturnUrlPlaceholder.PaymentGateway}&mobile_app=true`
          : `${WindowResource.getOrigin()}${generatePath(`/${SCREENS.ORDER_DETAILS}/${PaymentReturnUrlPlaceholder.OrderUUID}?payment_gateway=${PaymentReturnUrlPlaceholder.PaymentGateway}`)}`;

        return str(base).replaceAll(replace).toString();
      },
      [isMobileApp, generatePath],
    ),
  };
}

export function useHandleOrderCheckoutResult({ setLoading }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const alert = useAlert();
  const { navigate, reset } = useRouter();
  const [searchParams] = useSearchParams();
  const { onlinePaymentsGateway, selectedPaymentMethod } = usePaymentFlow();
  const { isMobileApp } = useMobileApp();
  const { openModal, closeModal } = useModalRouter();
  // Every gateway value is a PaymentGatewayKey — the old Adyen/Stripe ladder labelled Spreedly
  // failures 'unknown', splitting them off from the rest of the Spreedly trail in Sentry.
  const resultGateway: PaymentGatewayKey = onlinePaymentsGateway ?? 'unknown';

  function onPaymentError(options: PaymentErrorOptions = {}): boolean {
    // A cancelled confirmation already left its breadcrumb upstream; the customer still needs
    // the alert (the order is abandoned), but there is nothing for Sentry to alert on.
    if (!options.cancelled) {
      paymentLog.error(resultGateway, 'order_payment_unsuccessful', {
        payment_method: selectedPaymentMethod,
      });
    }

    setLoading(false);

    if (options.silent) {
      return false;
    }

    document.getElementById(SectionType.payment_info)?.scrollIntoView({
      behavior: 'smooth',
    });

    alert.error({
      marker: 'payment_unsuccessful',
      title: t('error_messages.payment_unsuccessful.title'),
      description: t('error_messages.payment_unsuccessful.description'),
    });

    if (onlinePaymentsGateway === PaymentGateway.Adyen || options.backToCheckout) {
      navigate({
        web: {
          to: SCREENS.PAYMENT,
        },
        mobile: {
          path: Routes.Payment,
        },
      });
    }

    isMobileApp && closeModal();

    return false;
  }

  function onPaymentSuccess(order: Pick<Order, 'uuid'>): boolean {
    setUtm(null);
    const updatedSearchParams = {
      redirect_status: RedirectStatus.succeeded,
      ...withoutOffsiteReturnParams(Object.fromEntries(searchParams)),
    };

    if (isMobileApp) {
      openModal({
        id: Modals.OrderTracker,
        presentation: null,
        props: {
          uuid: order.uuid,
          ...updatedSearchParams,
        },
        onDidPresent: () => reset(),
      });

      return true;
    }

    navigate({
      web: {
        to: {
          pathname: `${SCREENS.ORDER_DETAILS}/${order.uuid}`,
          search: createSearchParams(updatedSearchParams).toString(),
        },
      },
    });

    return true;
  }

  return {
    onPaymentError,
    onPaymentSuccess,
  };
}

export default function useOrderCheckout({ setLoading, onMobileAppCheckoutError }: Props) {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });
  const { t: tErrors } = useTranslation(undefined, { keyPrefix: 'Modules:Mobile:Shared:Errors' });
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { items } = useBasket();
  const { onLogin } = useAuth();
  const { navigate } = useRouter();
  const { refresh } = useInitialData();
  const { isMobileApp } = useMobileApp();
  const { openModal } = useModalRouter();
  const alert = useAlert();
  const { toggleInfoModal } = useAppMethods();
  const { trackBeginPurchase } = useAnalytics();
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    toggleCheckoutSection,
    onlinePaymentsGateway,
    paypalVariant,
  } = usePaymentFlow();
  const { handleValidationError } = useDeliveryOrderMethodValidation();
  const { toggleOrderMethodModal } = useAppMethods();
  const orderErrorHandler = useOrderErrorHandler();
  const orderMethodErrorHandlers = useOrderMethodErrorHandlers();
  const { onPaymentError, onPaymentSuccess } = useHandleOrderCheckoutResult({ setLoading });
  const {
    data,
    localData,
    loading: draftLoading,
    draftFailed,
    isDraftCurrent,
    isDraftSettling,
    createOrder,
    getRetryOrderUuid,
    setRetryOrderUuid,
    setValidationErrors,
    resetValidationErrors,
  } = useOrderFlow();
  const logGateway: PaymentGatewayKey = onlinePaymentsGateway ?? 'unknown';
  const runOtpStepUp = useOtpStepUp(logGateway);
  // Set while the one new order a refused retry falls back to is placed, so that checkout cannot
  // retry again, whatever the storage did with the slot.
  const placingFallbackOrderRef = useRef(false);

  const onPaymentConfirmationRequired = async (
    response: AxiosError<{ order: Order; payment_intent: PaymentIntent }, any>,
    confirmPayment: ConfirmPaymentFunction,
    loggedPaymentMethod: PaymentMethod | null,
  ): Promise<boolean> => {
    const order = response.response.data.order;
    const paymentIntent = response.response.data.payment_intent;

    paymentLog.info(logGateway, 'order_payment_confirmation_required', {
      order_uuid: order?.uuid,
      payment_method: loggedPaymentMethod,
      intent_id: paymentIntent?.id,
    });

    const { status: paymentStatus, error } = await confirmPayment(order, paymentIntent);

    if (paymentStatus === PaymentStatus.Failed) {
      // The customer backing out of a 3DS challenge is Failed for the order, but it is their
      // decision — a breadcrumb, not a Sentry issue (same rule as the Adyen CANCEL error).
      // A refusal by the bank or wallet is the customer's answer too, not ours to alert on.
      // A sheet abandoned with the page is the customer's decision too, and shown to nobody.
      const silent = error?.code === PaymentResultErrorCode.Abandoned;
      const cancelled =
        silent || error?.code === PaymentResultErrorCode.Cancelled || error?.code === PaymentResultErrorCode.Declined;

      if (cancelled) {
        paymentLog.info(logGateway, 'order_payment_cancelled', {
          order_uuid: order?.uuid,
          payment_method: loggedPaymentMethod,
        });
      } else {
        paymentLog.error(logGateway, 'order_payment_confirmation_failed', {
          order_uuid: order?.uuid,
          payment_method: loggedPaymentMethod,
        });
      }

      return onPaymentError({ cancelled, silent });
    }

    if (paymentStatus === PaymentStatus.Pending) {
      return false;
    }

    setRetryOrderUuid(null);

    return onPaymentSuccess(order);
  };

  const alertInternalError = () =>
    alert.error({
      marker: 'internal_error',
      title: t('error_messages.internal_error.title'),
      description: t('error_messages.internal_error.description'),
    });

  /**
   * The retry was refused: the order is gone (404) or no longer pending (403), e.g. an earlier attempt
   * settled by webhook. A new order is created only when nothing on the old one can still pay it, so
   * the customer is never charged twice. The backend reports `failed` when the order's latest attempt
   * has a decided outcome (it passes over earlier attempts a retry already cancelled), and
   * `awaiting_payment` while that attempt is still open.
   */
  const onRetryOrderRefused = async (
    uuid: string,
    placeNewOrder: () => Promise<boolean>,
    loggedPaymentMethod: PaymentMethod | null,
  ): Promise<boolean> => {
    const { success, status, response } = await OrderManager.getOrderPaymentStatus(uuid);
    const result = success ? (response as AxiosResponse<{ data: Partial<Order> }>)?.data?.data : null;
    const attrs = { order_uuid: uuid, payment_method: loggedPaymentMethod };

    if (!success && status === 404) {
      setRetryOrderUuid(null);
      paymentLog.warn(logGateway, 'order_retry_fell_back_to_post', { ...attrs, reason: 'not_found' });

      return placeNewOrder();
    }

    // Not knowing where the order stands is no reason to make another: the next tap asks again.
    if (!success || !result) {
      paymentLog.error(logGateway, 'order_retry_status_unknown', { ...attrs, status: status ?? 'none' });
      setLoading(false);
      alertInternalError();

      return false;
    }

    if (
      result.payment_status === ORDER_PAYMENT_STATUSES.PAID ||
      result.payment_status === ORDER_PAYMENT_STATUSES.PARTIALLY_REFUNDED
    ) {
      setRetryOrderUuid(null);
      paymentLog.info(logGateway, 'order_retry_already_paid', { ...attrs, payment_status: result.payment_status });

      return onPaymentSuccess({ uuid });
    }

    // A refunded order may still hold a second, captured payment, so no new order is placed for the
    // customer: they see the order as it stands, with the basket kept and no purchase reported.
    if (result.payment_status === ORDER_PAYMENT_STATUSES.REFUNDED) {
      setRetryOrderUuid(null);
      paymentLog.warn(logGateway, 'order_retry_refunded', attrs);
      setLoading(false);

      if (isMobileApp) {
        openModal({ id: Modals.OrderTracker, presentation: null, props: { uuid } });

        return false;
      }

      navigate({ web: { to: `${SCREENS.ORDER_DETAILS}/${uuid}` } });

      return false;
    }

    // Still pending, so the PUT was refused for something other than the order settling: a
    // contract surprise, whichever way it goes.
    if (result.payment_state === ORDER_PAYMENT_STATES.FAILED) {
      setRetryOrderUuid(null);
      paymentLog.issue(logGateway, 'order_retry_fell_back_to_post', { ...attrs, reason: 'refused_while_pending' });

      return placeNewOrder();
    }

    paymentLog.issue(logGateway, 'order_retry_attempt_in_flight', {
      ...attrs,
      payment_state: result.payment_state ?? 'none',
    });
    setLoading(false);
    alertInternalError();

    return false;
  };

  const onInputSectionError = (section: SectionType) => {
    navigate({
      web: {
        to: SCREENS.CHECKOUT,
      },
      mobile: {
        path: Routes.Checkout,
      },
    });
    toast.error(t('toasts.input_errors_found'));

    setTimeout(() => toggleCheckoutSection(section, true), 5);
  };

  // The payment section lives on its own screen on mobile web, not on the checkout one.
  const promptForPaymentMethod = (message?: string) => {
    navigate({
      web: {
        to: SCREENS.PAYMENT,
      },
      mobile: {
        path: Routes.Payment,
      },
    });
    toast.error(message ?? t('toasts.select_payment_method'));
    setTimeout(() => toggleCheckoutSection(SectionType.payment_info, true), 5);
  };

  const onValidationError = (response: ValidationErrorResponseData): boolean => {
    navigate({
      web: {
        to: SCREENS.CHECKOUT,
      },
      mobile: {
        path: Routes.Checkout,
      },
    });

    setValidationErrors(response);
    setLoading(false);

    // Navigate to personal info section on validation error
    if (
      collect(response.rules)
        .only(['customer_first_name', 'customer_last_name', 'customer_email', 'customer_phone_number'])
        .isNotEmpty()
    ) {
      onInputSectionError(SectionType.customer_info);

      return false;
    }

    // Navigate to invoice info section on validation error
    if (collect(response.rules).only(['invoice_email']).isNotEmpty()) {
      onInputSectionError(SectionType.invoice_info);

      return false;
    }

    // The CPF for Pix or NuPay, or a method this checkout cannot take: the payment section is where
    // the customer can fix it, with the backend's own words.
    if (collect(response.rules).only(['customer_document', 'payment_method_id']).isNotEmpty()) {
      const message = response.errors?.customer_document?.[0] ?? response.errors?.payment_method_id?.[0];

      promptForPaymentMethod(
        message ??
          (response.rules?.payment_method_id ? t('toasts.select_payment_method') : t('toasts.input_errors_found')),
      );

      return false;
    }

    // The backend turned the Stripe Elements checkout itself away: the restaurant switched it off,
    // the merchant hid the method for this order, or the payment details expired. Its words say
    // which; the company is read again so the payment section re-renders as the restaurant now has
    // it (the legacy checkout, or without the hidden method).
    if (collect(response.rules).only(STRIPE_ELEMENT_RULES).isNotEmpty()) {
      navigate({
        web: {
          to: SCREENS.PAYMENT,
        },
        mobile: {
          path: Routes.Payment,
        },
      });
      toast.error(stripeElementValidationMessage(response) ?? t('toasts.input_errors_found'));
      refresh(null, true);
      setTimeout(() => toggleCheckoutSection(SectionType.payment_info, true), 5);

      return false;
    }

    if (collect(response.rules).only(ORDER_METHOD_RULES).isNotEmpty()) {
      toggleOrderMethodModal({ openModal: true });

      return false;
    }

    // Anything else (a tip over the region cap, ...) is fixed on /checkout too, but has no section to
    // open — without the backend's words the customer is just bounced off /payment with no reason.
    toast.error(firstValidationMessage(response) ?? t('toasts.input_errors_found'));

    return false;
  };

  const _onMobileAppCheckoutError = (response: RenderableErrorResponse) => {
    navigate({
      web: {
        to: SCREENS.CHECKOUT,
      },
      mobile: {
        path: Routes.Checkout,
      },
    });

    const validationError = handleValidationError(response, tErrors('internal_error'));

    setLoading(false);

    if (validationError) {
      return openModal({
        id: Modals.OrderMethod,
        props: {
          deliveryAddressError: validationError.deliveryAddress,
          scheduledForError: validationError.scheduledFor,
        },
      });
    }

    orderErrorHandler(response);
    onMobileAppCheckoutError(response);
  };

  const onBadRequestError = async (
    response: AxiosResponse<{ error_code: string }>,
    retry: (headers: IMap<string>, perCallOptions?: CheckoutOptions) => Promise<boolean>,
    options?: CheckoutOptions,
  ): Promise<boolean> => {
    if (response.data.error_code === ERROR_CODES.ACCOUNT_SWITCH_REQUIRED) {
      await onLogin(response.data as unknown as LoginResponseInterface, false);
      setLoading(false);

      toggleInfoModal({
        openModal: true,
        title: t('modals.account_switch.title', { phoneNumber: localData.customer_phone_number }),
        description: t('modals.account_switch.description'),
        primaryButtonTitle: t('modals.account_switch.button'),
        onConfirm: () => window.location.reload(),
      });

      return true;
    }

    if (response.data.error_code !== ERROR_CODES.OTP_VERIFICATION_REQUIRED) {
      return false;
    }

    setLoading(false);

    // Allow callers (e.g. Adyen wallet flow) to dismiss external UI before
    // the OTP modal renders on top of it.
    options?.onBeforeOtpModalOpen?.();

    return runOtpStepUp(response.headers[OTP_VERIFICATION_PHONE_NUMBER_HEADER], retry);
  };

  const checkout: CheckoutFunction = async (confirmPayment, paymentIntent, extraData, extraHeaders, options) => {
    // The method createOrder charges, resolved the same way: extraData overrides the selection, and
    // PayPal-direct is charged as paypal_standalone (OrderFlowProvider.resolvePaymentMethodId). Its
    // gateway is what begin_purchase reports, so the funnel splits by the gateway that processes it.
    const { method: chargedPaymentMethod, source: paymentMethodSource } = resolveCheckoutPaymentMethod({
      explicit: extraData?.payment_method_id,
      explicitSource: extraData?.payment_method_source,
      selected:
        selectedPaymentMethod === PaymentMethod.Paypal && paypalVariant === PaypalVariant.Direct
          ? PaymentMethod.PaypalStandalone
          : selectedPaymentMethod,
      isFreeOrder: isFreeOrderDraft(data?.total, isDraftCurrent),
    });

    if (!chargedPaymentMethod) {
      paymentLog.warn(logGateway, 'order_checkout_no_payment_method', {
        draft_total: data?.total ?? 'none',
        is_draft_current: isDraftCurrent,
      });
      // A pay button may have raised its own spinner before calling in.
      setLoading(false);
      promptForPaymentMethod();

      return false;
    }

    setLoading(true);

    // What the backend charges through the Payment Element handler (PaymentMethodHandlerResolver):
    // Stripe, a ConfirmationToken, and not PayPal. Only these retry a failed order with a PUT; the
    // legacy Stripe paths, whose open intents a retry would not cancel, always create a new order.
    const isElementRequest =
      onlinePaymentsGateway === PaymentGateway.Stripe &&
      Boolean(extraData?.stripe_confirmation_token) &&
      extraData?.payment_method_id !== PaymentMethod.Paypal;
    const retryUuid = isElementRequest && !placingFallbackOrderRef.current ? getRetryOrderUuid() : null;

    // Under the Stripe Elements checkout, what the order is placed with: a method the caller names in
    // extraData (an Express Checkout wallet, a Payment Element type) wins over a stale selection. The
    // other gateways report the selection in the logs, and in begin_purchase the selection too
    // (PayPal-direct as paypal, not its charged paypal_standalone), except a Drop-in submit, charged as
    // what the shopper used there. The gateway follows the charged method.
    const placedPaymentMethod =
      onlinePaymentsGateway === PaymentGateway.Stripe
        ? (extraData?.payment_method_id ?? selectedPaymentMethod ?? chargedPaymentMethod)
        : null;
    const loggedPaymentMethod = placedPaymentMethod ?? selectedPaymentMethod;

    trackBeginPurchase(data, items, {
      payment_method:
        placedPaymentMethod ??
        (paymentMethodSource === 'adyen_dropin'
          ? chargedPaymentMethod
          : (selectedPaymentMethod ?? chargedPaymentMethod)),
      payment_gateway: resolveAnalyticsPaymentGateway(
        chargedPaymentMethod,
        company?.payment_gateways,
        company?.supported_payment_methods,
      ),
    });
    resetValidationErrors();

    paymentLog.info(logGateway, 'order_checkout_start', {
      payment_method: selectedPaymentMethod,
      charged_payment_method: chargedPaymentMethod,
      payment_method_source: paymentMethodSource,
      intent_id: paymentIntent?.id,
      is_retry: Boolean(extraHeaders && Object.keys(extraHeaders).length),
      is_retry_put: Boolean(retryUuid),
    });

    const { success, response, status } = await createOrder(
      paymentIntent,
      extraData ?? {},
      {
        [ERROR_CODES.ITEMS_OUT_OF_STOCK]: () =>
          refresh().then(() =>
            navigate({
              web: {
                to: SCREENS.HOME_SCREEN,
              },
            }),
          ),
        // Runs when the customer confirms the backend's "no cash here" modal: a persisted cash pick is
        // dropped so the pay button cannot send it again, and the customer is taken to choose a method.
        [ERROR_CODES.CASH_PAYMENTS_NOT_SUPPORTED]: () => {
          if (selectedPaymentMethod === PaymentMethod.Cash) {
            setSelectedPaymentMethod(null);
          }

          promptForPaymentMethod();
        },
        // Order-method 412s (pickup closed, slot taken, quote expired, ...) send the customer back
        // to /checkout with the order-method modal open, instead of the InfoModal's HOME fallback.
        ...orderMethodErrorHandlers,
      },
      extraHeaders,
      retryUuid,
    );

    // Signal "OTP gate cleared" to anyone listening (e.g. the OTP modal that
    // wants to close on /orders success instead of waiting for downstream
    // wallet/3DS flows). Anything other than 400 OTP_VERIFICATION_REQUIRED
    // counts as cleared — including validation errors, which are surfaced via
    // a separate UI channel.
    const isOtpStillFailing =
      status === 400 && (response as any)?.response?.data?.error_code === ERROR_CODES.OTP_VERIFICATION_REQUIRED;

    if (!isOtpStillFailing) {
      options?.onOtpAccepted?.();
    }

    const errorData = (response as any)?.response?.data;

    // The order a failed attempt leaves pending, kept for the next tap. A 402 is kept before its
    // confirm, so a challenge closed or a bank page left still has it after the redirect's reload.
    if (isElementRequest) {
      const failedOrderUuid =
        status === ReservedHttpResponseCodes.PaymentConfirmationRequired
          ? errorData?.order?.uuid
          : status === ReservedHttpResponseCodes.ErrorResponseModal &&
              errorData?.error_code === ERROR_CODES.CARD_EXCEPTION
            ? errorData?.payload?.order_uuid
            : null;

      if (failedOrderUuid) {
        setRetryOrderUuid(failedOrderUuid);
      }
    }

    const isRetryRefused =
      Boolean(retryUuid) &&
      (status === 404 || (status === 403 && errorData?.error_code !== ERROR_CODES.BLOCKED_FOR_FRAUD));

    if (isRetryRefused) {
      return await onRetryOrderRefused(
        retryUuid,
        async () => {
          placingFallbackOrderRef.current = true;

          try {
            return await checkout(confirmPayment, paymentIntent, extraData, extraHeaders, options);
          } finally {
            placingFallbackOrderRef.current = false;
          }
        },
        loggedPaymentMethod,
      );
    }

    if (success) {
      setRetryOrderUuid(null);
      paymentLog.info(logGateway, 'order_checkout_succeeded', {
        order_uuid: response.data?.data?.uuid,
        payment_method: loggedPaymentMethod,
      });

      return onPaymentSuccess(response.data.data);
    }

    if (status === ReservedHttpResponseCodes.PaymentConfirmationRequired) {
      return await onPaymentConfirmationRequired(response, confirmPayment, loggedPaymentMethod);
    }

    if (status === ReservedHttpResponseCodes.ValidationError) {
      paymentLog.warn(logGateway, 'order_checkout_validation_error', {
        payment_method: loggedPaymentMethod,
        rule_keys: Object.keys(response?.response?.data?.rules ?? {}).join(','),
      });

      return onValidationError(response.response.data);
    }

    // OTP and account-switch 400s are step-ups the customer can complete, not errors. They must
    // reach their modal on both platforms: the mobile branch below would otherwise swallow them into
    // the generic "unexpected problem" alert, because these responses carry no title/description.
    const badRequestErrorCode = status === 400 ? (response as any)?.response?.data?.error_code : null;
    const isCheckoutStepUp = [ERROR_CODES.OTP_VERIFICATION_REQUIRED, ERROR_CODES.ACCOUNT_SWITCH_REQUIRED].includes(
      badRequestErrorCode,
    );

    if (isCheckoutStepUp) {
      paymentLog.warn(logGateway, 'order_checkout_step_up_required', {
        payment_method: loggedPaymentMethod,
        error_code: badRequestErrorCode,
      });

      return await onBadRequestError(
        (response as any).response,
        (headers: IMap<string>, perCallOptions?: CheckoutOptions) =>
          checkout(confirmPayment, paymentIntent, extraData, headers, { ...options, ...perCallOptions }),
        options,
      );
    }

    if (
      status === ReservedHttpResponseCodes.ErrorResponseModal &&
      (response as any)?.response?.data?.error_code === ERROR_CODES.CASH_PAYMENTS_NOT_SUPPORTED
    ) {
      // Every one of these should have been a method the customer chose. Enough context to tell a
      // genuine cash pick from a fallback that should not exist.
      paymentLog.issue(logGateway, 'order_checkout_cash_not_supported', {
        selected_payment_method: selectedPaymentMethod,
        charged_payment_method: chargedPaymentMethod,
        payment_method_source: paymentMethodSource,
        draft_total: data?.total ?? 'none',
        draft_loading: draftLoading,
        draft_failed: draftFailed,
        draft_current: isDraftCurrent,
        draft_settling: isDraftSettling,
        gateway: onlinePaymentsGateway,
        adyen_method_type: extraData?.adyen_payment_data?.paymentMethod?.type,
        order_method: localData?.method,
        is_mobile_app: isMobileApp,
      });
    }

    if (isMobileApp) {
      paymentLog.error(logGateway, 'order_checkout_mobile_error', {
        payment_method: loggedPaymentMethod,
        status,
        error_code: (response as any)?.response?.data?.error_code,
        message: (response as any)?.message,
      });
      _onMobileAppCheckoutError(response as any as RenderableErrorResponse);

      return false;
    }

    if (status === 400) {
      paymentLog.warn(logGateway, 'order_checkout_bad_request', {
        payment_method: loggedPaymentMethod,
        error_code: response?.response?.data?.error_code,
      });

      return await onBadRequestError(
        response.response,
        (headers: IMap<string>, perCallOptions?: CheckoutOptions) =>
          checkout(confirmPayment, paymentIntent, extraData, headers, { ...options, ...perCallOptions }),
        options,
      );
    }

    if (
      status === ReservedHttpResponseCodes.ErrorResponseModal ||
      status === ReservedHttpResponseCodes.ErrorResponseAlertWindow
    ) {
      paymentLog.warn(logGateway, 'order_checkout_handled_error', {
        payment_method: loggedPaymentMethod,
        status,
        error_code: (response as any)?.response?.data?.error_code,
      });
      setLoading(false);

      return false;
    }

    if (status === ReservedHttpResponseCodes.TooManyRequests) {
      const retryAfterSeconds = Number((response as any)?.response?.headers?.['retry-after']) || 0;
      const unblockAt = retryAfterSeconds > 0 ? new Date(Date.now() + retryAfterSeconds * 1000) : null;
      const supportEmail = company?.service_provider?.support_email ?? 'support@foodamigos.io';

      paymentLog.warn(logGateway, 'order_checkout_throttled', {
        payment_method: loggedPaymentMethod,
        retry_after_seconds: retryAfterSeconds,
      });
      setLoading(false);
      alert.error({
        // Two markers for the two descriptions, mirroring the branch below: whether the backend
        // advertised a `retry-after` changes what the customer is told (a concrete unblock time vs
        // "contact support"), and only the marker distinguishes them without asserting on copy.
        marker: unblockAt ? 'order_blocked' : 'order_blocked_no_datetime',
        title: t('error_messages.order_blocked.title'),
        description: unblockAt
          ? t('error_messages.order_blocked.description', {
              datetime: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'short',
                timeStyle: 'short',
              }).format(unblockAt),
              supportEmail,
            })
          : t('error_messages.order_blocked.description_no_datetime', { supportEmail }),
      });

      return false;
    }

    // The backend's risk assessment refused the order outright (403 `blocked_for_fraud`). Unlike the
    // OTP step-up there is nothing the customer can complete here, so they get the same support
    // message as an open-ended throttle block rather than an "our tech team is on it" alert.
    if (status === 403 && response?.response?.data?.error_code === ERROR_CODES.BLOCKED_FOR_FRAUD) {
      const supportEmail = company?.service_provider?.support_email ?? 'support@foodamigos.io';

      paymentLog.warn(logGateway, 'order_checkout_blocked_for_fraud', {
        payment_method: loggedPaymentMethod,
      });
      setLoading(false);
      alert.error({
        marker: 'blocked_for_fraud',
        title: t('error_messages.order_blocked.title'),
        description: t('error_messages.order_blocked.description_no_datetime', { supportEmail }),
      });

      return false;
    }

    // A 4xx nothing above handles is a response this client was never taught to read — a code
    // gap. A 5xx is already the backend's Sentry issue, and no status at all is the network.
    const isUnhandledClientError = typeof status === 'number' && status >= 400 && status < 500;

    paymentLog[isUnhandledClientError ? 'issue' : 'error'](logGateway, 'order_checkout_internal_error', {
      payment_method: loggedPaymentMethod,
      status,
      message: (response as any)?.message,
    });
    setLoading(false);
    alertInternalError();

    return false;
  };

  return {
    checkout,
  };
}

export const ORDER_METHOD_RULES = [
  'room_service_location_id',
  'delivery_quote_id',
  'delivery_street_name',
  'delivery_street_number',
  'delivery_city',
  'delivery_zip_code',
];

export function firstValidationMessage(response: ValidationErrorResponseData): string | undefined {
  return Object.values(response.errors ?? {})[0]?.[0] ?? response.message;
}
